import { DatePickerInput } from "@impero-com/common/components/form/fields/datePickerInput";
import { SelectInput } from "@impero-com/common/components/form/fields/selectInput";
import InformationCircle from "@impero-com/common/img/information-circle.svg";
import { formatFinancialYear } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { TimezoneKey } from "@impero-com/common/utils/timezone";
import { Divider, FormLabel, Tooltip, Svg } from "@impero-com/ui";
import { Checkbox } from "antd";
import Radio from "antd/es/radio";
import { cx } from "class-variance-authority";
import moment, { Moment } from "moment-timezone";
import {
    Control,
    UseFormGetValues,
    UseFormSetValue,
    useController,
    useWatch,
} from "react-hook-form";

import {
    ControlWithFieldsResources,
    FinancialYearView,
    DueDateOffsetForMonthOffsetPeriod as MonthDueDateOffset,
    ControlRecurrenceRule,
    CalendarView,
} from "api/generated/model";
import { formatControlSchedulingType } from "org/control/utils";
import {
    COVERED_PERIOD_VALUES,
    CoveredPeriodKey,
    coveredPeriodToKey,
    keyToCoveredPeriod,
    translateCoveredPeriod,
} from "utils/coveredPeriod";

import { DEFAULT_DUE_DATE_OFFSET } from "../../list/controls/types";
import {
    DEFAULT_EVERY_2_MONTH_OCCURRENCE,
    DEFAULT_HALF_YEARLY_OCCURRENCE,
    DEFAULT_MONTHLY_OCCURRENCE,
    DEFAULT_QUARTERLY_OCCURRENCE,
} from "../consts";
import { ParsedControlScheduling } from "../formData";
import { ControlViewFormSchema } from "../formSchema";
import { useFinancialYearQuery } from "../queries/useFinancialYearQuery";
import { normalizeRecurrenceRule } from "../utils/normalizeRecurrenceRule";

import * as theme from "./ControlSchedule.theme.css";
import { ControlTriggering } from "./ControlTriggering";
import { DefaultMonthDueDateOffsetInput } from "./MonthDueDateOffsetInput";
import {
    OccurrenceTable,
    OccurrenceEntry,
    toOccurrenceEntries,
    updateOccurrenceEntries,
    isRuleWithOccurrence,
} from "./OccurrenceTable";
import { SendImmediatelyCheckbox } from "./SendImmediatelyCheckbox";
import * as styles from "./styles.css";
import { TimeAndTimezone } from "./TimeAndTimezone";
import { WeeklyRecurrence, isWeeklyRecurrenceRule } from "./WeeklyRecurrence";

export interface ControlScheduleProps {
    id: string;
    control: Control<ControlViewFormSchema>;
    getValues: UseFormGetValues<ControlViewFormSchema>;
    setValue: UseFormSetValue<ControlViewFormSchema>;
    disabled?: boolean;
    initialCalendars: CalendarView[];
    initialFinancialYear: FinancialYearView | null;
    controlId: string | null;
    initialTriggeredByControlId: string | null;
    linkedControls: ControlWithFieldsResources["controls"];
    linkedControlPrograms: ControlWithFieldsResources["controlPrograms"];
}

export function ControlSchedule({
    id,
    control,
    getValues,
    setValue,
    disabled,
    initialCalendars,
    initialFinancialYear,
    controlId,
    initialTriggeredByControlId,
    linkedControls,
    linkedControlPrograms,
}: ControlScheduleProps) {
    const { t } = useTranslation();
    const financialYear = useFinancialYearQuery(initialFinancialYear);

    // Read-only watches for conditional rendering
    const rule = useWatch({ control, name: "scheduling.rule" });
    const isRecurringMode = rule.type !== "Date";
    const recurringOrManual: RecurringOrManual = isRecurringMode
        ? "Recurring"
        : "Manual";

    const ruleHasDueDateOffset = rule.type !== "Date" && rule.type !== "Weekly";
    const ruleHasFinancialYear =
        "financialYearId" in rule && financialYear.data;
    const ruleHasOccurrences = isRuleWithOccurrence(rule);
    const ruleIsWeekly = isWeeklyRecurrenceRule(rule);

    // Convert schema scheduling (Date fields) to ParsedControlScheduling (Moment fields)
    const toParsed = (): ParsedControlScheduling => {
        const current = getValues("scheduling");
        return {
            ...current,
            timezoneName: current.timezoneName as TimezoneKey,
            dueTime: moment(current.dueTime),
            startDate: moment(current.startDate),
            sendTime: moment(getValues("allocatedTimes.sendTime")),
            remindersBefore: getValues("remindersBefore"),
            remindersAfter: getValues("remindersAfter"),
        };
    };

    // Cross-cutting: recurring/manual toggle resets rule + startDate + coveredPeriod + triggeredBy
    const handleRecurringOrManualChange = (value: RecurringOrManual) => {
        const updated = updateSchedulingViaRecurringOrManual(toParsed(), value);
        setValue("scheduling.rule", normalizeRecurrenceRule(updated.rule));
        setValue("scheduling.startDate", updated.startDate.toDate());
        setValue("scheduling.coveredPeriod", updated.coveredPeriod ?? null);
        setValue("triggeredBy", null);
        if (value === "Recurring") {
            setValue("sendImmediately", false);
        }
    };

    // Cross-cutting: interval change resets rule + coveredPeriod + triggeredBy
    const handleIntervalChange = (type: string | number | null | undefined) => {
        if (!type || !isRecurringMode) {
            return;
        }
        const updated = updateRecurringRule(
            toParsed() as Recurring,
            type as Recurring["rule"]["type"]
        );
        setValue("scheduling.rule", normalizeRecurrenceRule(updated.rule));
        setValue("scheduling.coveredPeriod", updated.coveredPeriod ?? null);
        setValue("triggeredBy", null);
    };

    const intervalOptions = INTERVAL_OPTIONS.map((key) => ({
        value: key,
        label: formatControlSchedulingType(key, t),
    }));

    const coveredPeriodOptions = Object.keys(COVERED_PERIOD_VALUES).map(
        (keyStr) => ({
            value: keyStr as CoveredPeriodKey,
            label: translateCoveredPeriod(keyStr as CoveredPeriodKey),
        })
    );

    const periodHelpText =
        recurringOrManual === "Manual"
            ? t("CreateControl_Control_CoveredPeriodManual_Tooltip")
            : t("CreateControl_Control_CoveredPeriod_Tooltip");

    return (
        <div id={id}>
            <div className={styles.marginBottom}>
                <Radio.Group
                    optionType="button"
                    value={recurringOrManual}
                    onChange={(e) =>
                        handleRecurringOrManualChange(
                            e.target.value as RecurringOrManual
                        )
                    }
                    options={[
                        {
                            value: "Recurring",
                            label: t("CreateControl_Recurrence_Recurring"),
                        },
                        {
                            value: "Manual",
                            label: t("CreateControl_Recurrence_Manual"),
                        },
                    ]}
                    buttonStyle="solid"
                    disabled={disabled}
                />
            </div>

            <div className={cx(styles.inputRow, styles.cancelMarginBottom)}>
                {isRecurringMode && (
                    <SelectInput
                        label={t("CreateControl_Recurrence_Interval")}
                        value={rule.type}
                        onChange={handleIntervalChange}
                        onBlur={() => {}}
                        options={intervalOptions}
                        disabled={disabled}
                    />
                )}
                <RHFStartDateInput
                    control={control}
                    setValue={setValue}
                    isRecurringMode={isRecurringMode}
                    disabled={disabled}
                />

                <RHFCoveredPeriodInput
                    control={control}
                    setValue={setValue}
                    isRecurringMode={isRecurringMode}
                    disabled={disabled}
                    options={coveredPeriodOptions}
                    helpText={periodHelpText}
                />
            </div>

            {ruleHasFinancialYear && (
                <RHFFinancialYearCheckbox
                    control={control}
                    financialYear={financialYear.data!}
                    disabled={disabled}
                />
            )}

            {ruleHasDueDateOffset && (
                <RHFDueDateOffsetSection
                    control={control}
                    ruleHasOccurrences={ruleHasOccurrences}
                    initialCalendars={initialCalendars}
                    disabled={disabled}
                />
            )}

            {recurringOrManual === "Manual" && (
                <div className={theme.marginTop}>
                    <FormLabel>
                        {t("CreateControl_Recurrence_SendImmediately")}
                    </FormLabel>
                    <SendImmediatelyCheckbox
                        control={control}
                        disabled={disabled}
                        className={theme.labelWithIcon}
                    />
                </div>
            )}

            {isRecurringMode && (
                <div className={theme.marginTop}>
                    <ControlTriggering
                        control={control}
                        controlId={controlId}
                        initialTriggeredByControlId={
                            initialTriggeredByControlId
                        }
                        linkedControls={linkedControls}
                        linkedControlPrograms={linkedControlPrograms}
                        disabled={disabled}
                    />
                </div>
            )}

            {ruleIsWeekly && (
                <>
                    <Divider />
                    <div className={styles.marginBottom}>
                        <WeeklyRecurrence
                            control={control}
                            disabled={disabled}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sub-components — each owns its controller(s)
// ---------------------------------------------------------------------------

function RHFStartDateInput({
    control,
    setValue,
    isRecurringMode,
    disabled,
}: {
    control: Control<ControlViewFormSchema>;
    setValue: UseFormSetValue<ControlViewFormSchema>;
    isRecurringMode: boolean;
    disabled?: boolean;
}) {
    const { t } = useTranslation();
    const { field } = useController({
        control,
        name: "scheduling.startDate",
        disabled,
    });

    const handleChange = (date: Date | null) => {
        if (date) {
            field.onChange(date);
            if (isRecurringMode) {
                setValue("triggeredBy", null);
            }
        }
    };

    if (!isRecurringMode) {
        return (
            <DatePickerInput
                label={t("CreateControl_Recurrence_DateRule_Label")}
                value={field.value}
                onChange={handleChange}
                disabled={disabled}
                nodeAfter={
                    <>
                        {t("CreateControl_Recurrence_At")}
                        <TimeAndTimezone
                            control={control}
                            disabled={disabled}
                        />
                    </>
                }
            />
        );
    }

    return (
        <DatePickerInput
            label={t("CreateControl_Recurrence_Interval_StartDate")}
            value={field.value}
            onChange={handleChange}
            disabled={disabled}
        />
    );
}

function RHFCoveredPeriodInput({
    control,
    setValue,
    isRecurringMode,
    disabled,
    options,
    helpText,
}: {
    control: Control<ControlViewFormSchema>;
    setValue: UseFormSetValue<ControlViewFormSchema>;
    isRecurringMode: boolean;
    disabled?: boolean;
    options: { value: CoveredPeriodKey; label: string }[];
    helpText: string;
}) {
    const { t } = useTranslation();
    const { field } = useController({
        control,
        name: "scheduling.coveredPeriod",
        disabled,
    });

    const handleChange = (key: string | number | null | undefined) => {
        field.onChange(keyToCoveredPeriod(key as CoveredPeriodKey));
        if (isRecurringMode) {
            setValue("triggeredBy", null);
        }
    };

    return (
        <SelectInput
            label={t("CreateControl_Control_CoveredPeriod")}
            helpText={helpText}
            value={coveredPeriodToKey(field.value)}
            onChange={handleChange}
            onBlur={field.onBlur}
            options={options}
            disabled={disabled}
        />
    );
}

function RHFFinancialYearCheckbox({
    control,
    financialYear,
    disabled,
}: {
    control: Control<ControlViewFormSchema>;
    financialYear: FinancialYearView;
    disabled?: boolean;
}) {
    const { t } = useTranslation();
    const { field } = useController({
        control,
        name: "scheduling.rule",
        disabled,
    });
    const rule = field.value;
    const isChecked =
        "financialYearId" in rule && rule.financialYearId !== null;

    const handleChange = (checked: boolean) => {
        if (!("financialYearId" in rule)) {
            return;
        }
        field.onChange({
            ...rule,
            financialYearId: checked ? financialYear.id : null,
        });
    };

    return (
        <div className={theme.marginTop}>
            <Checkbox
                checked={isChecked}
                onChange={(e) => handleChange(e.target.checked)}
                disabled={disabled}
            >
                <div className={theme.labelWithIcon}>
                    {t("CreateControl_Recurrence_UseFinancialYear")}
                    <Tooltip title={formatFinancialYear(financialYear)}>
                        <span>
                            <Svg
                                src={InformationCircle}
                                className={theme.infoIcon}
                            />
                        </span>
                    </Tooltip>
                </div>
            </Checkbox>
        </div>
    );
}

function RHFDueDateOffsetSection({
    control,
    ruleHasOccurrences,
    initialCalendars,
    disabled,
}: {
    control: Control<ControlViewFormSchema>;
    ruleHasOccurrences: boolean;
    initialCalendars: CalendarView[];
    disabled?: boolean;
}) {
    const { t } = useTranslation();
    const { field: ruleField } = useController({
        control,
        name: "scheduling.rule",
        disabled,
    });
    const rule = ruleField.value;

    const ruleWithOffset = rule as Extract<
        ControlRecurrenceRule,
        { defaultDueDateOffset: unknown }
    >;

    const handleDueDateOffsetChange = (offset: MonthDueDateOffset) => {
        ruleField.onChange({ ...rule, defaultDueDateOffset: offset });
    };

    const handleCalendarIdChange = (calendarId: number | null) => {
        ruleField.onChange({ ...rule, calendarId });
    };

    const handleOccurrencesChange = (entries: OccurrenceEntry[]) => {
        if (!isRuleWithOccurrence(rule)) {
            return;
        }
        ruleField.onChange(updateOccurrenceEntries(rule, entries));
    };

    return (
        <>
            <Divider />
            <div className={styles.marginBottom}>
                <DefaultMonthDueDateOffsetInput
                    label={t("ControlResult_DueDate")}
                    control={control}
                    value={ruleWithOffset.defaultDueDateOffset}
                    onChange={handleDueDateOffsetChange}
                    calendarId={ruleWithOffset.calendarId ?? null}
                    onCalendarIdChange={handleCalendarIdChange}
                    recurrenceType={
                        rule.type as Exclude<
                            ControlRecurrenceRule["type"],
                            "Date" | "Weekly"
                        >
                    }
                    initialCalendars={initialCalendars}
                    disabled={disabled}
                />
            </div>
            {ruleHasOccurrences && (
                <OccurrenceTable
                    control={control}
                    value={toOccurrenceEntries(
                        rule as Extract<
                            ControlRecurrenceRule,
                            { occurrences: object }
                        >
                    )}
                    onChange={handleOccurrencesChange}
                    defaultDueDateOffset={ruleWithOffset.defaultDueDateOffset}
                    calendarId={ruleWithOffset.calendarId ?? null}
                    recurrenceType={
                        rule.type as Exclude<
                            ControlRecurrenceRule["type"],
                            "Date" | "Weekly" | "Yearly"
                        >
                    }
                    initialCalendars={initialCalendars}
                    disabled={disabled}
                />
            )}
        </>
    );
}

// ---------------------------------------------------------------------------
// Pure helper functions
// ---------------------------------------------------------------------------

const INTERVAL_OPTIONS = [
    "Weekly",
    "Monthly",
    "EveryTwoMonths",
    "Quarterly",
    "HalfYearly",
    "Yearly",
] as const;

type RecurringOrManual = "Recurring" | "Manual";

function schedulingToRecurringOrManual(
    scheduling: ParsedControlScheduling
): RecurringOrManual {
    switch (scheduling.rule.type) {
        case "Date":
            return "Manual";
        case "Weekly":
        case "Monthly":
        case "EveryTwoMonths":
        case "Quarterly":
        case "HalfYearly":
        case "Yearly":
            return "Recurring";
        default:
            assertNever(scheduling.rule);
    }
}

function updateSchedulingViaRecurringOrManual(
    controlScheduling: ParsedControlScheduling,
    recurringOrManual: RecurringOrManual
): ParsedControlScheduling {
    if (
        recurringOrManual === schedulingToRecurringOrManual(controlScheduling)
    ) {
        return controlScheduling;
    }
    switch (recurringOrManual) {
        case "Manual":
            return {
                ...controlScheduling,
                rule: { type: "Date" },
                startDate: today().add(1, "days"),
                coveredPeriod: null,
            };
        case "Recurring":
            return {
                ...controlScheduling,
                rule: {
                    type: "Monthly",
                    calendarId: null,
                    defaultDueDateOffset: {
                        offsetPeriod: { months: 0, days: 0 },
                        offsetType: "AfterAnchor",
                        anchor: "EndOfInterval",
                    },
                    occurrences: DEFAULT_MONTHLY_OCCURRENCE,
                },
                startDate: today(),
                coveredPeriod: { type: "Month", data: 0 },
            };
    }
}

type Recurring = ParsedControlScheduling & {
    rule: Exclude<ControlRecurrenceRule, { type: "Date" }>;
};

function updateRecurringRule(
    controlScheduling: Recurring,
    type: Recurring["rule"]["type"]
): Recurring {
    if (controlScheduling.rule.type === type) {
        return controlScheduling;
    }

    const defaultDueDateOffset =
        controlScheduling.rule.type !== "Weekly"
            ? controlScheduling.rule.defaultDueDateOffset
            : DEFAULT_DUE_DATE_OFFSET;
    const calendarId =
        controlScheduling.rule.type !== "Weekly"
            ? controlScheduling.rule.calendarId
            : null;
    const financialYearId =
        "financialYearId" in controlScheduling.rule
            ? controlScheduling.rule.financialYearId
            : null;

    switch (type) {
        case "Weekly":
            return {
                ...controlScheduling,
                rule: { type: "Weekly", days: [], periodicity: 1 },
                coveredPeriod: null,
            };
        case "Monthly":
            return {
                ...controlScheduling,
                rule: {
                    type,
                    defaultDueDateOffset,
                    calendarId,
                    occurrences: DEFAULT_MONTHLY_OCCURRENCE,
                },
                coveredPeriod: { type: "Month", data: 0 },
            };
        case "EveryTwoMonths":
            return {
                ...controlScheduling,
                rule: {
                    type,
                    defaultDueDateOffset,
                    calendarId,
                    occurrences: DEFAULT_EVERY_2_MONTH_OCCURRENCE,
                },
                coveredPeriod: { type: "TwoMonths", data: 0 },
            };
        case "Quarterly":
        case "HalfYearly":
            return {
                ...controlScheduling,
                rule: {
                    type,
                    defaultDueDateOffset,
                    calendarId,
                    financialYearId,
                    occurrences:
                        type === "Quarterly"
                            ? DEFAULT_QUARTERLY_OCCURRENCE
                            : DEFAULT_HALF_YEARLY_OCCURRENCE,
                },
                coveredPeriod: {
                    type: type === "Quarterly" ? "Quarter" : "Half",
                    data: 0,
                },
            };
        case "Yearly":
            return {
                ...controlScheduling,
                rule: {
                    type,
                    defaultDueDateOffset,
                    calendarId,
                    financialYearId,
                    periodicity: 1,
                },
                coveredPeriod: { type: "Year", data: 0 },
            };
        default:
            assertNever(type);
    }
}

function today(): Moment {
    return moment().startOf("day");
}
